/*
 * This class is copied verbatim from Backstage 1.24.0.
 */

import Checkbox from '@material-ui/core/Checkbox';
import Grid from '@material-ui/core/Grid';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import partition from 'lodash/partition';
import { useCallback, useState } from 'react';
import { AnalyzeResult } from '../../api';
import { BackButton, NextButton } from '../Buttons';
import { EntityListComponent } from '../EntityListComponent';
import { PrepareResult } from '../useImportState';

type Props = {
  analyzeResult: Extract<AnalyzeResult, { type: 'locations' }>;
  prepareResult?: PrepareResult;
  onPrepare: (result: PrepareResult) => void;
  onGoBack?: () => void;
};

/**
 * A form that lets a user select one of a list of locations to import
 *
 * @param analyzeResult - the result of the analysis
 * @param prepareResult - the selectected locations from a previous step
 * @param onPrepare - called after the selection
 * @param onGoBack - called to go back to the previous step
 */
export const StepPrepareSelectLocations = ({
  analyzeResult,
  prepareResult,
  onPrepare,
  onGoBack,
}: Props) => {
  const [selectedUrls, setSelectedUrls] = useState<string[]>(
    prepareResult?.locations.map(l => l.target) || [],
  );

  const [existingLocations, locations] = partition(
    analyzeResult?.locations,
    l => l.exists,
  );

  const handleResult = useCallback(async () => {
    onPrepare({
      type: 'locations',
      locations: locations.filter((l: any) => selectedUrls.includes(l.target)),
    });
  }, [locations, onPrepare, selectedUrls]);

  const onItemClick = (url: string) => {
    setSelectedUrls(urls =>
      urls.includes(url) ? urls.filter(u => u !== url) : urls.concat(url),
    );
  };

  const onSelectAll = () => {
    setSelectedUrls(urls =>
      urls.length < locations.length ? locations.map(l => l.target) : [],
    );
  };

  return (
    <>
      {locations.length > 0 && (
        <>
          <Typography>
            Select one or more locations that are present in your git
            repository:
          </Typography>
          <EntityListComponent
            firstListItem={
              <ListItem dense button onClick={onSelectAll}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selectedUrls.length === locations.length}
                    indeterminate={
                      selectedUrls.length > 0 &&
                      selectedUrls.length < locations.length
                    }
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary="Select All" />
              </ListItem>
            }
            onItemClick={onItemClick}
            locations={locations}
            locationListItemIcon={target => (
              <Checkbox
                edge="start"
                checked={selectedUrls.includes(target)}
                tabIndex={-1}
                disableRipple
              />
            )}
            collapsed
          />
        </>
      )}

      {existingLocations.length > 0 && (
        <>
          <Typography>These locations already exist in the catalog:</Typography>
          <EntityListComponent
            locations={existingLocations}
            locationListItemIcon={() => <LocationOnIcon />}
            withLinks
            collapsed
          />
        </>
      )}

      <Grid container spacing={0}>
        {onGoBack && <BackButton onClick={onGoBack} />}
        <NextButton disabled={selectedUrls.length === 0} onClick={handleResult}>
          Review
        </NextButton>
      </Grid>
    </>
  );
};
